import React, { memo, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Box,
  Typography,
  Chip,
  IconButton,
  Button,
  Collapse,
  FormGroup,
  FormControlLabel,
  Checkbox,
  useMediaQuery,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import {
  Edit,
  Delete,
  ExpandMore,
  ExpandLess,
  AdminPanelSettings,
  Person,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { getInitials, formatCelular } from '../../utils/userUtils';
import { USER_ROLES, THEME_COLORS, BREAKPOINTS, AVAILABLE_PERMISSIONS } from '../../constants';

/**
 * Componente de tabla de usuarios para vista de escritorio
 */
const UsersTable = memo(({
  users,
  expandedUserId,
  onExpand,
  onEdit,
  onDelete,
  showActions = true,
  // Props de paginación
  pagination = null,
  onPageChange = null,
  onPageSizeChange = null,
}) => {
  const isTablet = useMediaQuery(`(max-width:${BREAKPOINTS.TABLET}px)`);

  // Componente de controles de paginación
  const PaginationControls = () => {
    if (!pagination) return null;

    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: 2,
          borderTop: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
          backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT,
        }}
      >
        {/* Información de registros */}
        <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_SECONDARY }}>
          Mostrando {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total} usuarios
        </Typography>

        {/* Controles de navegación */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Selector de elementos por página */}
          <FormControl size="small" sx={{ minWidth: 80 }}>
            <Select
              value={pagination.limit}
              onChange={(e) => onPageSizeChange?.(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
                },
                '& .MuiSelect-select': {
                  color: THEME_COLORS.TEXT_PRIMARY,
                  fontSize: '0.875rem',
                },
              }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
          </FormControl>

          {/* Botones de navegación */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              disabled={!pagination.hasPrev}
              onClick={() => onPageChange?.(pagination.page - 1)}
              startIcon={<NavigateBeforeIcon />}
              sx={{
                color: THEME_COLORS.TEXT_SECONDARY,
                borderColor: THEME_COLORS.BORDER_LIGHT,
                borderRadius: '8px',
                textTransform: 'none',
                '&:hover': {
                  borderColor: THEME_COLORS.TEXT_PRIMARY,
                  color: THEME_COLORS.TEXT_PRIMARY,
                },
                '&:disabled': {
                  color: THEME_COLORS.TEXT_MUTED,
                  borderColor: THEME_COLORS.BORDER_LIGHT,
                },
              }}
            >
              Anterior
            </Button>

            <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_PRIMARY, px: 2 }}>
              Página {pagination.page} de {pagination.pages || 1}
            </Typography>

            <Button
              variant="outlined"
              size="small"
              disabled={!pagination.hasNext}
              onClick={() => onPageChange?.(pagination.page + 1)}
              endIcon={<NavigateNextIcon />}
              sx={{
                color: THEME_COLORS.TEXT_SECONDARY,
                borderColor: THEME_COLORS.BORDER_LIGHT,
                borderRadius: '8px',
                textTransform: 'none',
                '&:hover': {
                  borderColor: THEME_COLORS.TEXT_PRIMARY,
                  color: THEME_COLORS.TEXT_PRIMARY,
                },
                '&:disabled': {
                  color: THEME_COLORS.TEXT_MUTED,
                  borderColor: THEME_COLORS.BORDER_LIGHT,
                },
              }}
            >
              Siguiente
            </Button>
          </Box>
        </Box>
      </Box>
    );
  };

  // Componente de fila vacía
  const EmptyTableRow = memo(() => (
    <TableRow>
      <TableCell colSpan={showActions ? 5 : 4} sx={{ textAlign: 'center', padding: 4 }}>
        <Typography variant="body1" sx={{ color: THEME_COLORS.TEXT_MUTED }}>
          No se encontraron usuarios que coincidan con la búsqueda
        </Typography>
      </TableCell>
    </TableRow>
  ));

  // Componente de header de tabla
  const TableHeader = memo(() => (
    <TableHead>
      <TableRow sx={{ backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT }}>
        <TableCell sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_PRIMARY, fontSize: '0.875rem' }}>
          Usuario
        </TableCell>
        <TableCell sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_PRIMARY, fontSize: '0.875rem' }}>
          Contacto
        </TableCell>
        <TableCell sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_PRIMARY, fontSize: '0.875rem' }}>
          Rol
        </TableCell>
        <TableCell sx={{ fontWeight: 600, color: THEME_COLORS.TEXT_PRIMARY, fontSize: '0.875rem' }}>
          Permisos
        </TableCell>
        {showActions && (
          <TableCell sx={{ 
            fontWeight: 600, 
            color: THEME_COLORS.TEXT_PRIMARY, 
            textAlign: 'center',
            fontSize: '0.875rem',
          }}>
            Acciones
          </TableCell>
        )}
      </TableRow>
    </TableHead>
  ));

  // Componente de fila de usuario
  const UserRow = memo(({ user }) => {
    const isAdmin = user.rol === USER_ROLES.ADMIN;
    const isExpanded = expandedUserId === user.user_id;

    return (
      <React.Fragment>
        <TableRow 
          sx={{ 
            '&:hover': { 
              backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
            },
            transition: 'background-color 0.2s ease-in-out',
          }}
        >
          {/* Usuario */}
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: isAdmin ? THEME_COLORS.PRIMARY : THEME_COLORS.SECONDARY,
                  marginRight: 2,
                  fontSize: '0.9rem',
                  fontWeight: 600,
                }}
              >
                {getInitials(user.nombre)}
              </Avatar>
              <Typography
                variant="body2"
                sx={{ 
                  fontWeight: 500, 
                  color: THEME_COLORS.TEXT_PRIMARY,
                  fontSize: '0.875rem',
                }}
              >
                {user.nombre}
              </Typography>
            </Box>
          </TableCell>

          {/* Contacto */}
          <TableCell>
            <Box>
              <Typography
                variant="body2"
                sx={{
                  color: THEME_COLORS.TEXT_PRIMARY,
                  fontSize: '0.875rem',
                }}
              >
                {user.correo}
              </Typography>
              {user.celular && (
                <Typography
                  variant="caption"
                  sx={{
                    color: THEME_COLORS.TEXT_SECONDARY,
                    fontSize: '0.75rem',
                  }}
                >
                  {formatCelular(user.celular)}
                </Typography>
              )}
            </Box>
          </TableCell>

          {/* Rol */}
          <TableCell>
            <Chip
              icon={isAdmin ? <AdminPanelSettings /> : <Person />}
              label={isAdmin ? 'Administrador' : 'Colaborador'}
              size="small"
              sx={{
                backgroundColor: isAdmin ? THEME_COLORS.PRIMARY : THEME_COLORS.SECONDARY,
                color: 'white',
                fontSize: '0.75rem',
                fontWeight: 500,
              }}
            />
          </TableCell>

          {/* Permisos */}
          <TableCell>
            <Button
              size="small"
              variant="outlined"
              onClick={() => onExpand(user.user_id)}
              endIcon={isExpanded ? <ExpandLess /> : <ExpandMore />}
              sx={{
                borderColor: THEME_COLORS.BORDER_LIGHT,
                color: THEME_COLORS.TEXT_SECONDARY,
                fontSize: '0.75rem',
                textTransform: 'none',
                borderRadius: '8px',
                '&:hover': {
                  borderColor: THEME_COLORS.PRIMARY,
                  backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
                  color: THEME_COLORS.PRIMARY,
                },
              }}
            >
              {isExpanded ? 'Ocultar' : 'Ver'}
            </Button>
          </TableCell>

          {/* Acciones */}
          {showActions && (
            <TableCell sx={{ textAlign: 'center' }}>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                <IconButton 
                  size="small" 
                  sx={{ 
                    color: THEME_COLORS.SECONDARY,
                    '&:hover': {
                      backgroundColor: THEME_COLORS.LIGHT_GRAY,
                      color: THEME_COLORS.PRIMARY,
                    },
                  }}
                  onClick={() => onEdit(user)}
                >
                  <Edit fontSize="small" />
                </IconButton>
                <IconButton 
                  size="small" 
                  sx={{ 
                    color: THEME_COLORS.SECONDARY,
                    '&:hover': {
                      backgroundColor: '#ffebee',
                      color: '#d32f2f',
                    },
                  }}
                  onClick={() => onDelete(user)}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            </TableCell>
          )}
        </TableRow>

        {/* Fila expandible de permisos */}
        <TableRow>
          <TableCell colSpan={showActions ? 5 : 4} sx={{ padding: 0, border: 'none' }}>
            <Collapse in={isExpanded}>
              <Box sx={{ 
                padding: 3, 
                backgroundColor: THEME_COLORS.BACKGROUND_DEFAULT, 
                borderTop: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
              }}>
                <Typography
                  variant="subtitle2"
                  sx={{ 
                    marginBottom: 2, 
                    fontWeight: 600, 
                    color: THEME_COLORS.TEXT_PRIMARY,
                    fontSize: '0.9rem',
                  }}
                >
                  Permisos de {user.nombre}
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: isTablet ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                    gap: 2,
                  }}
                >
                  {AVAILABLE_PERMISSIONS.map((permiso) => (
                    <FormControlLabel
                      key={permiso.key}
                      control={
                        <Checkbox
                          checked={user.permisos?.[permiso.key] || false}
                          disabled
                          size="small"
                          sx={{
                            color: THEME_COLORS.SECONDARY,
                            '&.Mui-checked': {
                              color: THEME_COLORS.PRIMARY,
                            },
                            '&.Mui-disabled': {
                              opacity: 0.6,
                            },
                          }}
                        />
                      }
                      label={
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontSize: '0.825rem',
                            color: THEME_COLORS.TEXT_SECONDARY,
                          }}
                        >
                          {permiso.label}
                        </Typography>
                      }
                    />
                  ))}
                </Box>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  });

  UserRow.displayName = 'UserRow';

  if (users.length === 0) {
    return (
      <Box>
        <TableContainer
          component={Paper}
          sx={{
            border: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
            borderRadius: '12px',
            overflow: 'hidden',
            backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
          }}
        >
          <Table>
            <TableHeader />
            <TableBody>
              <EmptyTableRow />
            </TableBody>
          </Table>
        </TableContainer>
        <PaginationControls />
      </Box>
    );
  }

  return (
    <Box>
      <TableContainer
        component={Paper}
        sx={{
          border: `1px solid ${THEME_COLORS.BORDER_LIGHT}`,
          borderRadius: '12px',
          overflow: 'hidden',
          backgroundColor: THEME_COLORS.BACKGROUND_PAPER,
          boxShadow: `0 4px 12px ${THEME_COLORS.SHADOW_LIGHT}`,
        }}
      >
        <Table>
          <TableHeader />
          <TableBody>
            {users.map((user) => (
              <UserRow key={user.user_id} user={user} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <PaginationControls />
    </Box>
  );
});

UsersTable.displayName = 'UsersTable';

export default UsersTable; 